import React from 'react';

import { Field } from 'formik';
import { startOfToday, subDays } from 'date-fns';
import { InputLabel, TextField, TextFieldProps } from '@mui/material';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';

interface CustomDateTimePickerProps {
  name: string;
  label: string;
  minDate?: number;
  disabled?: boolean;
  isMandatory?: boolean;
}

/**
 * A date-time picker based on the MUI KeyboardDateTimePicker
 * Back-dated to 45 days.
 */
const CustomDateTimePicker = ({
  name,
  label,
  minDate,
  disabled = false,
  isMandatory = false,
}: CustomDateTimePickerProps) => {
  return (
    <>
      <Field name={name}>
        {({ field, form }: { field: any; form: any }) => {
          const fieldError: string = form.errors[name];
          const hasError = Boolean(form.touched[name]) && Boolean(fieldError);
          return (
            <>
              <InputLabel
                htmlFor={`${name}-dateTimePicker-field`}
                required={isMandatory}
                disabled={disabled}
                error={hasError}
              >
                {label}
              </InputLabel>
              <LocalizationProvider
                dateAdapter={MomentUtils as any}
                dateFormats={{
                  normalDate: 'DD.MM.YYYY HH:mm',
                  keyboardDate: 'DD.MM.YYYY HH:mm',
                }}
              >
                <DateTimePicker
                  disabled={disabled}
                  style={{ width: '100%' }}
                  {...field}
                  mask="__.__.____ __:__"
                  InputProps={{ disableUnderline: true }}
                  ampm={false}
                  onChange={(date: any) => {
                    form.setFieldTouched(field.name, true, true);
                    form.setFieldValue(
                      field.name,
                      moment(date).isValid()
                        ? moment(date).local(true).toDate()
                        : date === null
                          ? date
                          : date.toString(),
                    );
                  }}
                  renderInput={(props: TextFieldProps) => {
                    return (
                      <TextField
                        {...props}
                        fullWidth
                        error={hasError}
                        id={`${name}-dateTimePicker-field`}
                        data-testid={`${name}-dateTimePicker-field`}
                        helperText={hasError && (fieldError.includes('Invalid') ? 'Invalid date format!' : fieldError)}
                        variant="filled"
                      />
                    );
                  }}
                  minDate={minDate ? subDays(startOfToday(), minDate) : undefined}
                  maxDate={new Date(4000, 0, 0, 0, 0, 0, 0)}
                  minDateMessage={`A contact note can be backdated to a maximum of ${minDate} days!`}
                  id={`${name}-dateTimePicker`}
                  helperText={field.error}
                  variant="filled"
                  inputVariant="filled"
                  inputFormat="DD.MM.YYYY HH:mm"
                  components={{
                    OpenPickerIcon: CalendarIcon,
                  }}
                  OpenPickerButtonProps={{
                    style: { marginTop: '2px' },
                    disableRipple: true,
                  }}
                />
              </LocalizationProvider>
            </>
          );
        }}
      </Field>
    </>
  );
};

export default CustomDateTimePicker;
